import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import TopTabs from '../../components/common/TopTabs';
import AiUsagePanel from '../../components/admin/AiUsagePanel';
import OrgAccessPanel from '../../components/admin/OrgAccessPanel';
import OrgInsightsPanel from '../../components/admin/OrgInsightsPanel';
import SubscriptionPanel from '../../components/admin/SubscriptionPanel';
import { SystemAdminDataService, SystemAdminOrganisation } from '../../services/systemAdminDataService';
import { useUrlTab } from '../../hooks/useUrlTab';

type OrgTab = 'subscription' | 'activity' | 'aiUsage' | 'access';

/**
 * System Admin → Organisations → one organisation. Sub-tabs per concern:
 * Subscription (tier, capacity, entitlements), Activity (how much the
 * organisation actually uses the product — aggregates only), AI Usage (platform cost
 * telemetry, deliberately not shown to the organisation's own admins) and
 * Access (who can administer the org, plus the break-glass grant that is the
 * only way back when nobody can). Built to grow: add a TopTab entry + panel
 * per future concern (config, support…).
 */
const OrganisationDetail: React.FC = () => {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = parseInt(orgIdParam ?? '', 10);
  // Subscription leads: it answers "what are they on?", which is the question
  // that brings you to this page. AI Usage is a drill-down from there.
  //
  // Two-way as of the shared hook: this used to read ?tab= on mount only, so
  // the Overview table's deep link worked but switching tabs left the URL
  // behind — refreshing or sharing then landed on Subscription.
  const [tab, setTab] = useUrlTab<OrgTab>(
    'tab',
    ['subscription', 'activity', 'aiUsage', 'access'],
    'subscription',
  );
  const [org, setOrg] = useState<SystemAdminOrganisation | null>(null);

  // Name the org in the header. The list is small and cached upstream is
  // absent, so refetching it here is the simplest source of truth.
  useEffect(() => {
    if (!Number.isInteger(orgId)) return;
    let cancelled = false;
    SystemAdminDataService.fetchOrganisations()
      .then(orgs => { if (!cancelled) setOrg(orgs.find(o => o.id === orgId) ?? null); })
      .catch(() => { /* header just shows the id */ });
    return () => { cancelled = true; };
  }, [orgId]);

  if (!Number.isInteger(orgId) || orgId <= 0) {
    return <Navigate to="/system-admin/organisations" replace />;
  }

  return (
    <div className="sys-page">
      <div className="sys-detail__head">
        <Link to="/system-admin/organisations" className="sys-back" title="Back to organisations">
          <i className="fas fa-arrow-left" aria-hidden="true" />Organisations
        </Link>
        <h1 className="sys-detail__name">{org ? org.name : `Organisation #${orgId}`}</h1>
        {org && !org.isActive && (
          <span className="sys-state sys-state--off"><i className="fas fa-minus-circle" />Inactive</span>
        )}
      </div>

      <TopTabs<OrgTab>
        ariaLabel="Organisation sections"
        active={tab}
        onChange={setTab}
        compact
        tabs={[
          { id: 'subscription', label: 'Subscription', icon: 'fa-file-invoice' },
          { id: 'activity', label: 'Activity', icon: 'fa-chart-line' },
          { id: 'aiUsage', label: 'AI Usage', icon: 'fa-robot' },
          { id: 'access', label: 'Access', icon: 'fa-user-shield' },
        ]}
      />

      {tab === 'subscription' && (
        <SubscriptionPanel orgId={orgId} orgName={org?.name ?? `Organisation #${orgId}`} />
      )}
      {tab === 'activity' && <OrgInsightsPanel orgId={orgId} />}
      {tab === 'aiUsage' && <AiUsagePanel orgId={orgId} />}
      {tab === 'access' && (
        <OrgAccessPanel orgId={orgId} orgName={org?.name ?? `Organisation #${orgId}`} />
      )}
    </div>
  );
};

export default OrganisationDetail;
